// API Service - central handler for the Get It Done Scheduler

const API_BASE = '/api';

// Generic fetch wrapper with error handling
async function fetchAPI(endpoint, options = {}) {
  const url = `${API_BASE}${endpoint}`;

  const defaultOptions = {
    headers: { 'Content-Type': 'application/json' },
    credentials: 'include' // include cookies for session auth
  };

  const finalOptions = {
    ...defaultOptions,
    ...options,
    headers: { ...defaultOptions.headers, ...options.headers }
  };

  try {
    const response = await fetch(url, finalOptions);
    const data = await response.json();
    if (!response.ok) {
      return { success: false, error: data.error || `HTTP ${response.status}`, status: response.status };
    }
    return { success: true, ...data };
  } catch (error) {
    console.error(`API Error [${endpoint}]:`, error);
    return { success: false, error: error.message || 'Network error' };
  }
}

// Multipart helper — no Content-Type header so the browser sets the boundary.
async function uploadAPI(endpoint, formData) {
  try {
    const response = await fetch(`${API_BASE}${endpoint}`, { method: 'POST', body: formData, credentials: 'include' });
    const data = await response.json();
    if (!response.ok) return { success: false, error: data.error || `HTTP ${response.status}` };
    return { success: true, ...data };
  } catch (error) {
    return { success: false, error: error.message || 'Network error' };
  }
}

function qs(params = {}) {
  const clean = Object.fromEntries(Object.entries(params).filter(([, v]) => v !== undefined && v !== null && v !== ''));
  const s = new URLSearchParams(clean).toString();
  return s ? `?${s}` : '';
}

// ============================================
// AUTH
// ============================================
export const authService = {
  login: (email, password) => fetchAPI('/auth/login', { method: 'POST', body: JSON.stringify({ email, password }) }),
  logout: () => fetchAPI('/auth/logout', { method: 'POST' }),
  me: () => fetchAPI('/auth/me')
};

// ============================================
// CLIENTS (the customers table, surfaced as Clients)
// ============================================
export const clientsService = {
  getAll: (params = {}) => fetchAPI(`/clients${qs(params)}`),
  getById: (id) => fetchAPI(`/clients/${id}`),
  create: (client) => fetchAPI('/clients', { method: 'POST', body: JSON.stringify(client) }),
  update: (id, client) => fetchAPI(`/clients/${id}`, { method: 'PUT', body: JSON.stringify(client) }),
  delete: (id) => fetchAPI(`/clients/${id}`, { method: 'DELETE' }),
  getAppointments: (id) => fetchAPI(`/clients/${id}/appointments`),
  mapData: () => fetchAPI('/clients/map-data'),
  geocode: (id) => fetchAPI(`/clients/${id}/geocode`, { method: 'POST' })
};

// ============================================
// STAFF (the team — bookable people)
// ============================================
export const staffService = {
  getAll: () => fetchAPI('/staff'),
  getById: (id) => fetchAPI(`/staff/${id}`),
  me: () => fetchAPI('/staff/me'),
  updateMe: (data) => fetchAPI('/staff/me', { method: 'PUT', body: JSON.stringify(data) }),
  create: (person) => fetchAPI('/staff', { method: 'POST', body: JSON.stringify(person) }),
  update: (id, person) => fetchAPI(`/staff/${id}`, { method: 'PUT', body: JSON.stringify(person) }),
  deactivate: (id) => fetchAPI(`/staff/${id}`, { method: 'DELETE' })
};

// ============================================
// SERVICES (per-person meeting types)
// ============================================
export const servicesService = {
  getAll: (params = {}) => fetchAPI(`/services${qs(params)}`),
  getById: (id) => fetchAPI(`/services/${id}`),
  create: (service) => fetchAPI('/services', { method: 'POST', body: JSON.stringify(service) }),
  update: (id, service) => fetchAPI(`/services/${id}`, { method: 'PUT', body: JSON.stringify(service) }),
  delete: (id) => fetchAPI(`/services/${id}`, { method: 'DELETE' })
};

// ============================================
// AVAILABILITY (working hours + exceptions + slot preview)
// ============================================
export const availabilityService = {
  getWorkingHours: (params = {}) => fetchAPI(`/availability/working-hours${qs(params)}`),
  updateWorkingHours: (working_hours) => fetchAPI('/availability/working-hours', { method: 'PUT', body: JSON.stringify({ working_hours }) }),
  getExceptions: (params = {}) => fetchAPI(`/availability/exceptions${qs(params)}`),
  addException: (exception) => fetchAPI('/availability/exceptions', { method: 'POST', body: JSON.stringify(exception) }),
  deleteException: (id) => fetchAPI(`/availability/exceptions/${id}`, { method: 'DELETE' }),
  getSlots: (params = {}) => fetchAPI(`/availability/slots${qs(params)}`)
};

// ============================================
// APPOINTMENTS
// ============================================
export const appointmentsService = {
  getAll: (params = {}) => fetchAPI(`/appointments${qs(params)}`),
  getById: (id) => fetchAPI(`/appointments/${id}`),
  create: (appointment) => fetchAPI('/appointments', { method: 'POST', body: JSON.stringify(appointment) }),
  update: (id, appointment) => fetchAPI(`/appointments/${id}`, { method: 'PUT', body: JSON.stringify(appointment) }),
  delete: (id) => fetchAPI(`/appointments/${id}`, { method: 'DELETE' }),
  updateStatus: (id, status) => fetchAPI(`/appointments/${id}/status`, { method: 'PATCH', body: JSON.stringify({ status }) }),
  reschedule: (id, startsAt, ignoreHours = false, durationMinutes = null) => fetchAPI(`/appointments/${id}/reschedule`, { method: 'POST', body: JSON.stringify({ startsAt, ignoreHours, ...(durationMinutes ? { durationMinutes } : {}) }) }),
  getCalendar: (start, end, params = {}) => fetchAPI(`/appointments/calendar${qs({ start, end, ...params })}`),
  getDashboard: (params = {}) => fetchAPI(`/appointments/dashboard${qs(params)}`)
};

// ============================================
// SETTINGS
// ============================================
export const settingsService = {
  get: () => fetchAPI('/settings'),
  update: (settings) => fetchAPI('/settings', { method: 'PUT', body: JSON.stringify(settings) }),
  mapsConfig: () => fetchAPI('/settings/maps')
};

// ============================================
// RATE CARD (surfaces priced per m² / lineal m for the map estimator)
// ============================================
export const surfaceRatesService = {
  getAll: (params = {}) => fetchAPI(`/surface-rates${qs(params)}`),
  saveAll: (rates) => fetchAPI('/surface-rates', { method: 'PUT', body: JSON.stringify({ rates }) }),
  create: (rate) => fetchAPI('/surface-rates', { method: 'POST', body: JSON.stringify(rate) }),
  update: (id, rate) => fetchAPI(`/surface-rates/${id}`, { method: 'PUT', body: JSON.stringify(rate) }),
  delete: (id) => fetchAPI(`/surface-rates/${id}`, { method: 'DELETE' })
};

// ============================================
// PUBLIC BOOKING (unauthenticated /api/public/*)
// ============================================
export const imagesService = {
  forAppointment: (id) => fetchAPI(`/appointments/${id}/images`),
  upload: (id, files, kind) => {
    const fd = new FormData();
    for (const f of files) fd.append('images', f);
    fd.append('kind', kind);
    return uploadAPI(`/appointments/${id}/images`, fd);
  },
  delete: (id) => fetchAPI(`/images/${id}`, { method: 'DELETE' }),
  fileUrl: (id, variant) => `${API_BASE}/images/${id}/file${variant === 'thumb' ? '?variant=thumb' : ''}`,
  gallery: () => fetchAPI('/gallery')
};

export const publicBookingService = {
  listServices: () => fetchAPI('/public/services'),
  listStaff: () => fetchAPI('/public/staff'),
  getStaffServices: (slug) => fetchAPI(`/public/staff/${slug}/services`),
  getSlots: (serviceId, from, to) => fetchAPI(`/public/services/${serviceId}/slots${qs({ from, to })}`),
  createBooking: (payload) => fetchAPI('/public/bookings', { method: 'POST', body: JSON.stringify(payload) }),
  uploadBookingImages: (token, files) => {
    const fd = new FormData();
    for (const f of files) fd.append('images', f);
    return uploadAPI(`/public/bookings/${token}/images`, fd);
  },
  getBooking: (token) => fetchAPI(`/public/bookings/${token}`),
  cancelBooking: (token) => fetchAPI(`/public/bookings/${token}/cancel`, { method: 'POST' }),
  rescheduleBooking: (token, startsAt) => fetchAPI(`/public/bookings/${token}/reschedule`, { method: 'POST', body: JSON.stringify({ startsAt }) })
};


// ============================================
// QUOTES (admin) + PUBLIC QUOTE VIEW (/api/public/quotes/*)
// ============================================
export const quotesService = {
  getAll: (params = {}) => fetchAPI(`/quotes${qs(params)}`),
  getById: (id) => fetchAPI(`/quotes/${id}`),
  create: (quote) => fetchAPI('/quotes', { method: 'POST', body: JSON.stringify(quote) }),
  update: (id, quote) => fetchAPI(`/quotes/${id}`, { method: 'PUT', body: JSON.stringify(quote) }),
  delete: (id) => fetchAPI(`/quotes/${id}`, { method: 'DELETE' }),
  send: (id) => fetchAPI(`/quotes/${id}/send`, { method: 'POST' }),
  setStatus: (id, status) => fetchAPI(`/quotes/${id}/status`, { method: 'POST', body: JSON.stringify({ status }) }),
  link: (id, appointmentId) => fetchAPI(`/quotes/${id}/link`, { method: 'POST', body: JSON.stringify({ appointment_id: appointmentId }) }),
  pdfUrl: (id) => `${API_BASE}/quotes/${id}/pdf`,
  materialsPdfUrl: (id) => `${API_BASE}/quotes/${id}/materials.pdf`,
  mapUrl: (id, v = '') => `${API_BASE}/quotes/${id}/map.png${v ? `?v=${encodeURIComponent(v)}` : ''}`
};

export const publicQuotesService = {
  get: (token) => fetchAPI(`/public/quotes/${token}`),
  accept: (token, name) => fetchAPI(`/public/quotes/${token}/accept`, { method: 'POST', body: JSON.stringify({ name }) }),
  decline: (token, reason) => fetchAPI(`/public/quotes/${token}/decline`, { method: 'POST', body: JSON.stringify({ reason }) }),
  pdfUrl: (token) => `${API_BASE}/public/quotes/${token}/pdf`,
  mapUrl: (token) => `${API_BASE}/public/quotes/${token}/map.png`
};

export default {
  auth: authService,
  clients: clientsService,
  staff: staffService,
  services: servicesService,
  availability: availabilityService,
  appointments: appointmentsService,
  settings: settingsService,
  surfaceRates: surfaceRatesService,
  publicBooking: publicBookingService,
  quotes: quotesService,
  publicQuotes: publicQuotesService
};

/**
 * Quote worksheet (GID-Session-024, #773 + #774) — internal costings behind a
 * quote and the materials list built from the same lines. Never public.
 */
export const worksheetService = {
  get: (quoteId) => fetchAPI(`/quotes/${quoteId}/worksheet`),
  addLine: (quoteId, line) => fetchAPI(`/quotes/${quoteId}/work-lines`, { method: 'POST', body: JSON.stringify(line) }),
  updateLine: (lineId, line) => fetchAPI(`/work-lines/${lineId}`, { method: 'PUT', body: JSON.stringify(line) }),
  deleteLine: (lineId) => fetchAPI(`/work-lines/${lineId}`, { method: 'DELETE' }),
  saveNotes: (quoteId, notes) => fetchAPI(`/quotes/${quoteId}/worksheet-notes`, { method: 'PUT', body: JSON.stringify(notes) }),
  forJob: (jobId) => fetchAPI(`/jobs/${jobId}/worksheet`)
};

/**
 * Internal job costing (GID-Session-009) — cost lines + receipt photos per
 * appointment, and the cost categories edited under Settings.
 */
export const costsService = {
  forAppointment: (id) => fetchAPI(`/appointments/${id}/costs`),
  addLine: (id, line) => fetchAPI(`/appointments/${id}/costs`, { method: 'POST', body: JSON.stringify(line) }),
  forJob: (jobId) => fetchAPI(`/jobs/${jobId}/costs`),
  addJobLine: (jobId, line) => fetchAPI(`/jobs/${jobId}/costs`, { method: 'POST', body: JSON.stringify(line) }),
  updateLine: (lineId, line) => fetchAPI(`/costs/${lineId}`, { method: 'PUT', body: JSON.stringify(line) }),
  deleteLine: (lineId) => fetchAPI(`/costs/${lineId}`, { method: 'DELETE' }),
  categories: (params = {}) => fetchAPI(`/cost-categories${qs(params)}`),
  travelForJob: (jobId) => fetchAPI(`/jobs/${jobId}/travel`),
  saveCategories: (categories) => fetchAPI('/cost-categories', { method: 'PUT', body: JSON.stringify({ categories }) }),
  deleteCategory: (id) => fetchAPI(`/cost-categories/${id}`, { method: 'DELETE' })
};

/**
 * Jobs (GID-Session-010) — the parent record of visits (appointments), quotes,
 * costs and photos.
 */
export const jobsService = {
  getAll: (params = {}) => fetchAPI(`/jobs${qs(params)}`),
  getById: (id) => fetchAPI(`/jobs/${id}`),
  create: (job) => fetchAPI('/jobs', { method: 'POST', body: JSON.stringify(job) }),
  update: (id, job) => fetchAPI(`/jobs/${id}`, { method: 'PUT', body: JSON.stringify(job) }),
  delete: (id) => fetchAPI(`/jobs/${id}`, { method: 'DELETE' }),
  attachQuote: (id, quoteId) => fetchAPI(`/jobs/${id}/attach-quote`, { method: 'POST', body: JSON.stringify({ quote_id: quoteId }) }),
  attachVisit: (id, appointmentId) => fetchAPI(`/jobs/${id}/attach-visit`, { method: 'POST', body: JSON.stringify({ appointment_id: appointmentId }) }),
  schedule: (id, plan) => fetchAPI(`/jobs/${id}/schedule`, { method: 'POST', body: JSON.stringify(plan) })
};

/**
 * Business costs / overheads (GID-Session-015, backlog #681) — expenses with
 * receipts, the equipment register and the overhead rate every job carries.
 */
export const businessCostsService = {
  overview: () => fetchAPI('/business-costs'),
  overheads: () => fetchAPI('/overheads'),
  addExpense: (expense) => fetchAPI('/expenses', { method: 'POST', body: JSON.stringify(expense) }),
  updateExpense: (id, expense) => fetchAPI(`/expenses/${id}`, { method: 'PUT', body: JSON.stringify(expense) }),
  deleteExpense: (id) => fetchAPI(`/expenses/${id}`, { method: 'DELETE' }),
  addEquipment: (item) => fetchAPI('/equipment', { method: 'POST', body: JSON.stringify(item) }),
  updateEquipment: (id, item) => fetchAPI(`/equipment/${id}`, { method: 'PUT', body: JSON.stringify(item) }),
  deleteEquipment: (id) => fetchAPI(`/equipment/${id}`, { method: 'DELETE' }),
  // Loans — cash only (GID-Session-016)
  loans: () => fetchAPI('/loans'),
  addLoan: (loan) => fetchAPI('/loans', { method: 'POST', body: JSON.stringify(loan) }),
  updateLoan: (id, loan) => fetchAPI(`/loans/${id}`, { method: 'PUT', body: JSON.stringify(loan) }),
  deleteLoan: (id) => fetchAPI(`/loans/${id}`, { method: 'DELETE' }),
  addLoanPayment: (id, payment) => fetchAPI(`/loans/${id}/payments`, { method: 'POST', body: JSON.stringify(payment) }),
  deleteLoanPayment: (id) => fetchAPI(`/loan-payments/${id}`, { method: 'DELETE' }),
  // The interest slice a payment on a date would carry (GID-Session-017, backlog #683)
  suggestInterest: (id, params) => fetchAPI(`/loans/${id}/suggest${qs(params)}`),
  // "Who paid" ledger between partners (GID-Session-017, backlog #685)
  ledger: () => fetchAPI('/ledger'),
  saveShares: (shares) => fetchAPI('/ledger/shares', { method: 'PUT', body: JSON.stringify({ shares }) }),
  addSettlement: (settlement) => fetchAPI('/settlements', { method: 'POST', body: JSON.stringify(settlement) }),
  deleteSettlement: (id) => fetchAPI(`/settlements/${id}`, { method: 'DELETE' }),
  // Expenses + job cost lines together, one table (GID-Session-017, backlog #686)
  purchases: (params = {}) => fetchAPI(`/purchases${qs(params)}`)
};

/**
 * Receipts (GID-Session-018, backlog #687) — one docket, many lines. A receipt
 * is a record of its own (supplier, date, who paid, pages) that expenses, job
 * cost lines and equipment point at; `kind` is 'expense' | 'job_cost' | 'equipment'.
 */
export const receiptsService = {
  list: (params = {}) => fetchAPI(`/receipts${qs(params)}`),
  get: (id) => fetchAPI(`/receipts/${id}`),
  // The whole docket in one go: header + page photos + every line on it → { receipt, expenses, equipment, job_costs }
  create: ({ header, lines, files = [] }) => {
    const fd = new FormData();
    for (const f of files) fd.append('pages', f);
    fd.append('payload', JSON.stringify({ header, lines }));
    return uploadAPI('/receipts', fd);
  },
  update: (id, header) => fetchAPI(`/receipts/${id}`, { method: 'PUT', body: JSON.stringify(header) }),
  remove: (id, { force = false } = {}) => fetchAPI(`/receipts/${id}${force ? '?force=1' : ''}`, { method: 'DELETE' }),
  addPages: (id, files) => {
    const fd = new FormData();
    for (const f of files) fd.append('pages', f);
    return uploadAPI(`/receipts/${id}/pages`, fd);
  },
  deletePage: (pageId) => fetchAPI(`/receipt-pages/${pageId}`, { method: 'DELETE' }),
  pageUrl: (pageId, variant) => `${API_BASE}/receipt-pages/${pageId}/file${variant === 'thumb' ? '?variant=thumb' : ''}`,
  // The camera icon on a row: photos for this row — more pages of its receipt, or a new receipt made for it
  uploadFor: (kind, rowId, files) => {
    const fd = new FormData();
    for (const f of files) fd.append('pages', f);
    return uploadAPI(`/receipts/for/${kind}/${rowId}`, fd);
  },
  attach: (receiptId, kind, rowId) => fetchAPI(`/receipts/${receiptId}/links`, { method: 'PUT', body: JSON.stringify({ kind, id: rowId }) }),
  detach: (kind, rowId) => fetchAPI(`/receipts/links/${kind}/${rowId}`, { method: 'DELETE' })
};

/** Reports (GID-Session-016) — P&L by month, business costs in the period, equipment utilisation. */
export const reportsService = {
  periods: () => fetchAPI('/reports/periods'),
  pl: (params = {}) => fetchAPI(`/reports/pl${qs(params)}`),
  utilisation: (params = {}) => fetchAPI(`/reports/utilisation${qs(params)}`),
  // Anchors, not fetch, so the session cookie rides along (GID-Session-017, backlog #683)
  costsCsvUrl: (params = {}) => `${API_BASE}/reports/costs.csv${qs(params)}`,
  plCsvUrl: (params = {}) => `${API_BASE}/reports/pl.csv${qs(params)}`
};

/** PII retention (GID-Session-017, backlog #611) — stale clients and anonymising them. */
export const retentionService = {
  stale: (params = {}) => fetchAPI(`/retention/stale${qs(params)}`),
  anonymise: (ids) => fetchAPI('/retention/anonymise', { method: 'POST', body: JSON.stringify({ ids }) })
};

/** Review requests after a job (GID-Session-017, backlog #612). */
export const reviewsService = {
  send: (jobId) => fetchAPI(`/jobs/${jobId}/review-request`, { method: 'POST' }),
  cancel: (jobId) => fetchAPI(`/jobs/${jobId}/review-request`, { method: 'DELETE' })
};

/**
 * Letterbox drops (GID-Session-019, backlog #691) — a campaign of the ~100
 * letterboxes around a job, each with its lot from the cadastre, its traced
 * driveway and what happened on the walk.
 */
export const dropsService = {
  getAll: () => fetchAPI('/drops'),
  preview: (body) => fetchAPI('/drops/preview', { method: 'POST', body: JSON.stringify(body) }),
  create: (body) => fetchAPI('/drops', { method: 'POST', body: JSON.stringify(body) }),
  getById: (id) => fetchAPI(`/drops/${id}`),
  update: (id, body) => fetchAPI(`/drops/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  delete: (id) => fetchAPI(`/drops/${id}`, { method: 'DELETE' }),
  updateProperty: (id, pid, body) => fetchAPI(`/drops/${id}/properties/${pid}`, { method: 'PUT', body: JSON.stringify(body) }),
  saveMeasurements: (id, pid, measurements) => fetchAPI(`/drops/${id}/properties/${pid}/measurements`, { method: 'PUT', body: JSON.stringify({ measurements }) }),
  // An outline drawn on the map ([[lat, lng], ...]): what it would add, then add it (backlog #694)
  extendPreview: (id, polygon) => fetchAPI(`/drops/${id}/extend/preview`, { method: 'POST', body: JSON.stringify({ polygon }) }),
  extend: (id, polygon) => fetchAPI(`/drops/${id}/extend`, { method: 'POST', body: JSON.stringify({ polygon }) }),
  csvUrl: (id) => `/api/drops/${id}/export.csv`
};

/**
 * Smart-quote requests (GID-Session-022): the public "send us your address" form,
 * and the photos it sent as the Job page shows them.
 */
export const quoteRequestsService = {
  create: (payload) => fetchAPI('/public/quote-requests', { method: 'POST', body: JSON.stringify(payload) }),
  uploadImages: (token, files) => {
    const fd = new FormData();
    for (const f of files) fd.append('images', f);
    return uploadAPI(`/public/quote-requests/${token}/images`, fd);
  },
  imageUrl: (id, variant = 'full') => `${API_BASE}/quote-requests/images/${id}/file${variant === 'thumb' ? '?variant=thumb' : ''}`
};

/** Website visits (GID-Session-022) — the Dashboard tile; the public beacon is utils/visitBeacon.js. */
export const visitsService = {
  summary: (days = 7) => fetchAPI(`/visits/summary${qs({ days })}`)
};

/**
 * Content Studio (backlog #780, GID-Session-025): Facebook posts, the AI creative
 * partner and scheduled publishing. AI calls start a background job; pollAi waits
 * for it (an Opus batch can take a minute or two).
 */
export const contentService = {
  status: () => fetchAPI('/content/status'),
  voice: () => fetchAPI('/content/voice'),
  saveVoice: (text) => fetchAPI('/content/voice', { method: 'PUT', body: JSON.stringify({ text }) }),
  posts: (params = {}) => fetchAPI(`/content/posts${qs(params)}`),
  get: (id) => fetchAPI(`/content/posts/${id}`),
  create: (body) => fetchAPI('/content/posts', { method: 'POST', body: JSON.stringify(body) }),
  update: (id, body) => fetchAPI(`/content/posts/${id}`, { method: 'PUT', body: JSON.stringify(body) }),
  delete: (id) => fetchAPI(`/content/posts/${id}`, { method: 'DELETE' }),
  approve: (id, body) => fetchAPI(`/content/posts/${id}/approve`, { method: 'POST', body: JSON.stringify(body) }),
  unapprove: (id) => fetchAPI(`/content/posts/${id}/unapprove`, { method: 'POST' }),
  retry: (id) => fetchAPI(`/content/posts/${id}/retry`, { method: 'POST' }),
  fromIdea: (id) => fetchAPI(`/content/posts/${id}/from-idea`, { method: 'POST' }),
  addPhotos: (id, imageIds = [], libraryIds = []) => fetchAPI(`/content/posts/${id}/media`, { method: 'POST', body: JSON.stringify({ image_ids: imageIds, library_ids: libraryIds }) }),
  upload: (id, files) => {
    const fd = new FormData();
    for (const f of files) fd.append('images', f);
    return uploadAPI(`/content/posts/${id}/media/upload`, fd);
  },
  // before / after: a job photo id, or { job: id } / { library: id }
  beforeAfter: (id, before, after, layout = 'auto') => {
    const side = (k, r) => (r && typeof r === 'object' && r.library ? { [`${k}_library_id`]: r.library } : { [`${k}_id`]: r && typeof r === 'object' ? r.job : r });
    return fetchAPI(`/content/posts/${id}/before-after`, { method: 'POST', body: JSON.stringify({ ...side('before', before), ...side('after', after), layout }) });
  },
  reorder: (id, ids) => fetchAPI(`/content/posts/${id}/media/order`, { method: 'PUT', body: JSON.stringify({ ids }) }),
  deleteMedia: (mediaId) => fetchAPI(`/content/media/${mediaId}`, { method: 'DELETE' }),
  mediaUrl: (mediaId, variant) => `${API_BASE}/content/media/${mediaId}/file${variant === 'thumb' ? '?variant=thumb' : ''}`,
  jobs: () => fetchAPI('/content/jobs'),
  // Our photo library (loaded from our own computers and phones)
  library: (params = {}) => fetchAPI(`/content/library${qs(params)}`),
  uploadLibrary: (file, fields = {}) => {
    const fd = new FormData();
    fd.append('image', file, file.name);
    for (const [k, v] of Object.entries(fields)) if (v !== undefined && v !== null && v !== '') fd.append(k, v);
    return uploadAPI('/content/library', fd);
  },
  updateLibrary: (ids, fields) => fetchAPI('/content/library', { method: 'PUT', body: JSON.stringify({ ids, ...fields }) }),
  pairLibrary: (beforeId, afterId) => fetchAPI('/content/library/pair', { method: 'POST', body: JSON.stringify({ before_id: beforeId, after_id: afterId }) }),
  unpairLibrary: (id) => fetchAPI(`/content/library/${id}/unpair`, { method: 'POST' }),
  deleteLibrary: (ids) => fetchAPI('/content/library/delete', { method: 'POST', body: JSON.stringify({ ids }) }),
  libraryUrl: (id, variant) => `${API_BASE}/content/library/${id}/file${variant === 'thumb' ? '?variant=thumb' : ''}`,
  sortLibrary: (ids) => fetchAPI('/content/library/ai/sort', { method: 'POST', body: JSON.stringify(ids ? { ids } : {}) }),
  setSocials: (jobId, ok) => fetchAPI(`/content/jobs/${jobId}/socials`, { method: 'PUT', body: JSON.stringify({ ok }) }),
  ideas: (brief, count) => fetchAPI('/content/ai/ideas', { method: 'POST', body: JSON.stringify({ brief, count }) }),
  captions: (id, tweak) => fetchAPI(`/content/posts/${id}/ai/captions`, { method: 'POST', body: JSON.stringify({ tweak }) }),
  photoCheck: (jobId) => fetchAPI(`/content/jobs/${jobId}/ai/photo-check`, { method: 'POST' }),
  aiJob: (jobId) => fetchAPI(`/content/ai/jobs/${jobId}`),
  // Pictures made with AI (backlog #782: Get It Posted's gpt-image engine). make/change/ideas are AI jobs (pollAi).
  pictures: (params = {}) => fetchAPI(`/content/pictures${qs(params)}`),
  cast: () => fetchAPI('/content/pictures/cast'),
  saveCast: (cast) => fetchAPI('/content/pictures/cast', { method: 'PUT', body: JSON.stringify({ cast }) }),
  pictureIdeas: (brief, postId) => fetchAPI('/content/pictures/ideas', { method: 'POST', body: JSON.stringify({ brief, post_id: postId || null }) }),
  makePicture: (body) => fetchAPI('/content/pictures/make', { method: 'POST', body: JSON.stringify(body) }),
  changePicture: (id, change, quality) => fetchAPI(`/content/pictures/${id}/change`, { method: 'POST', body: JSON.stringify({ change, quality }) }),
  pictureWords: (id, body) => fetchAPI(`/content/pictures/${id}/words`, { method: 'POST', body: JSON.stringify(body) }),
  deletePicture: (id) => fetchAPI(`/content/pictures/${id}`, { method: 'DELETE' }),
  checkPicture: (id) => fetchAPI(`/content/pictures/${id}/check`, { method: 'POST' }),
  ratePicture: (id, body) => fetchAPI(`/content/pictures/${id}/rate`, { method: 'POST', body: JSON.stringify(body) }),
  lessons: () => fetchAPI('/content/pictures/lessons'),
  saveLessons: (lessons) => fetchAPI('/content/pictures/lessons', { method: 'PUT', body: JSON.stringify({ lessons }) }),
  pictureUrl: (id, variant) => `${API_BASE}/content/pictures/${id}/file${variant === 'thumb' ? '?variant=thumb' : ''}`,
  addPicture: (postId, pictureId) => fetchAPI(`/content/posts/${postId}/media/picture`, { method: 'POST', body: JSON.stringify({ picture_id: pictureId }) }),
  /** Start → poll until done. Resolves { success, result } or { success:false, error }. */
  async pollAi(start, { intervalMs = 2000, timeoutMs = 5 * 60 * 1000, onProgress } = {}) {
    const started = await start;
    if (!started.success) return started;
    const t0 = Date.now();
    for (;;) {
      await new Promise((r) => setTimeout(r, intervalMs));
      const r = await fetchAPI(`/content/ai/jobs/${started.job.id}`);
      if (!r.success) return r;
      if (r.job.status === 'done') return { success: true, result: r.job.result };
      if (r.job.status === 'error') return { success: false, error: r.job.error };
      if (onProgress && r.job.progress) onProgress(r.job.progress);
      if (Date.now() - t0 > timeoutMs) return { success: false, error: 'The AI is taking too long. Try again.' };
    }
  }
};
